import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Link2, Plus, Trash2, RefreshCw, Loader2, AlertCircle,
  Crown, Shield, Users, Settings, ArrowRight, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '../services/api';

// SSO Service
const ssoService = {
  async getMappings() {
    const response = await apiClient.get('/sso/mappings');
    return response.data;
  },
  async createMapping(data) {
    const response = await apiClient.post('/sso/mappings', data);
    return response.data;
  },
  async deleteMapping(mappingId) {
    const response = await apiClient.delete(`/sso/mappings/${mappingId}`);
    return response.data;
  },
  async getFallbackConfig() {
    const response = await apiClient.get('/sso/fallback');
    return response.data;
  },
  async updateFallbackConfig(data) {
    const response = await apiClient.put('/sso/fallback', data);
    return response.data;
  },
  async getRolesForMapping() {
    const response = await apiClient.get('/sso/roles-for-mapping');
    return response.data;
  },
  async getTenantsForMapping() {
    const response = await apiClient.get('/sso/tenants-for-mapping');
    return response.data;
  },
  async testMapping(entraName, entraType) {
    const response = await apiClient.post(`/sso/test-mapping?entra_name=${encodeURIComponent(entraName)}&entra_type=${entraType}`);
    return response.data;
  }
};

export const SSOMapppingPanel = () => {
  const [mappings, setMappings] = useState({ app_role_mappings: [], group_mappings: [] });
  const [fallbackConfig, setFallbackConfig] = useState({});
  const [roles, setRoles] = useState({ global_roles: [], tenant_roles: [] });
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showFallbackDialog, setShowFallbackDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // New mapping form state
  const [newMapping, setNewMapping] = useState({
    entra_type: 'group',
    entra_id: '',
    entra_name: '',
    portal_role_id: '',
    portal_tenant_id: '',
    sync_on_login: true
  });

  // Fallback form state
  const [fallbackForm, setFallbackForm] = useState({
    strategy: 'default_role',
    default_role_id: 'role_viewer',
    default_tenant_id: 'tenant_demo_001'
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [mappingsData, fallbackData, rolesData, tenantsData] = await Promise.all([
        ssoService.getMappings(),
        ssoService.getFallbackConfig(),
        ssoService.getRolesForMapping(),
        ssoService.getTenantsForMapping()
      ]);
      setMappings(mappingsData);
      setFallbackConfig(fallbackData);
      setFallbackForm(fallbackData);
      setRoles(rolesData);
      setTenants(tenantsData.tenants || []);
    } catch (error) {
      toast.error('Fehler beim Laden der SSO-Konfiguration');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateMapping = async () => {
    if (!newMapping.entra_name || !newMapping.portal_role_id) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    if (newMapping.entra_type === 'group' && !newMapping.portal_tenant_id) {
      toast.error('Bitte wählen Sie einen Mandanten für Gruppen-Mappings');
      return;
    }

    setActionLoading(true);
    try {
      await ssoService.createMapping(newMapping);
      toast.success('Mapping erstellt');
      setShowCreateDialog(false);
      setNewMapping({
        entra_type: 'group',
        entra_id: '',
        entra_name: '',
        portal_role_id: '',
        portal_tenant_id: '',
        sync_on_login: true
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Mappings');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteMapping = async (mappingId, isDefault) => {
    if (isDefault) {
      toast.error('Standard-Mappings können nicht gelöscht werden');
      return;
    }

    if (!window.confirm('Mapping wirklich löschen?')) return;

    setActionLoading(true);
    try {
      await ssoService.deleteMapping(mappingId);
      toast.success('Mapping gelöscht');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveFallback = async () => {
    setActionLoading(true);
    try {
      await ssoService.updateFallbackConfig(fallbackForm);
      toast.success('Fallback-Konfiguration gespeichert');
      setShowFallbackDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setActionLoading(false);
    }
  };

  const MappingCard = ({ mapping, isAppRole }) => (
    <div className="flex items-center justify-between p-4 border rounded-lg bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-4">
        <div className={`p-2 rounded-lg ${isAppRole ? 'bg-amber-50' : 'bg-blue-50'}`}>
          {isAppRole ? <Crown className="w-5 h-5 text-amber-600" /> : <Users className="w-5 h-5 text-blue-600" />}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{mapping.entra_name}</span>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <span className="text-slate-600">{mapping.portal_role_display || mapping.portal_role_name}</span>
          </div>
          <div className="flex gap-2 mt-1">
            <Badge variant="outline" className="text-xs">
              {isAppRole ? 'App Rolle' : 'Gruppe'}
            </Badge>
            {mapping.portal_tenant_name && (
              <Badge variant="secondary" className="text-xs">
                {mapping.portal_tenant_name}
              </Badge>
            )}
            {mapping.is_default && (
              <Badge className="text-xs bg-slate-100 text-slate-600">Standard</Badge>
            )}
            {mapping.sync_on_login && (
              <Badge className="text-xs bg-green-50 text-green-700">Sync aktiv</Badge>
            )}
          </div>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleDeleteMapping(mapping.mapping_id, mapping.is_default)}
        disabled={mapping.is_default || actionLoading}
      >
        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
      </Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            SSO Rollen-Mapping
          </h3>
          <p className="text-sm text-slate-500">
            Verknüpfung von Entra ID Gruppen/Rollen mit Portal-Rollen
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
          <Button 
            size="sm" 
            onClick={() => setShowCreateDialog(true)}
            style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Neues Mapping
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <Tabs defaultValue="mappings" className="space-y-4">
          <TabsList className="bg-slate-100 border border-slate-200 p-1">
            <TabsTrigger value="mappings" className="data-[state=active]:bg-white">
              Mappings ({mappings.app_role_mappings?.length + mappings.group_mappings?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="fallback" className="data-[state=active]:bg-white">
              Fallback-Strategie
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mappings" className="space-y-4">
            {/* App Role Mappings */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  App Rollen-Mappings (Global)
                </CardTitle>
                <CardDescription>
                  Entra App Roles werden auf globale Portal-Rollen gemappt
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {mappings.app_role_mappings?.length > 0 ? (
                  mappings.app_role_mappings.map(m => (
                    <MappingCard key={m.mapping_id} mapping={m} isAppRole={true} />
                  ))
                ) : (
                  <p className="text-sm text-slate-500 p-4 bg-slate-50 rounded">
                    Keine App Rollen-Mappings konfiguriert
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Group Mappings */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  Gruppen-Mappings (Mandantenspezifisch)
                </CardTitle>
                <CardDescription>
                  Entra Gruppen werden auf mandantenspezifische Portal-Rollen gemappt
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {mappings.group_mappings?.length > 0 ? (
                  mappings.group_mappings.map(m => (
                    <MappingCard key={m.mapping_id} mapping={m} isAppRole={false} />
                  ))
                ) : (
                  <div className="text-center py-8 bg-slate-50 rounded">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500 mb-4">
                      Keine Gruppen-Mappings konfiguriert
                    </p>
                    <p className="text-xs text-slate-400 max-w-md mx-auto">
                      Namenskonvention: <code className="bg-slate-100 px-1 rounded">Portal_TenantSlug_Rolle</code>
                      <br />
                      Beispiel: Portal_Acme_Admin, Portal_Glasmatic_Supporter
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="fallback">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Fallback-Strategie
                </CardTitle>
                <CardDescription>
                  Was passiert, wenn ein SSO-Benutzer keine passenden Gruppen/Rollen hat?
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">
                        {fallbackConfig.strategy === 'deny' && 'Zugriff verweigern'}
                        {fallbackConfig.strategy === 'default_role' && 'Standard-Rolle zuweisen'}
                        {fallbackConfig.strategy === 'pending_approval' && 'Admin-Genehmigung erforderlich'}
                      </p>
                      {fallbackConfig.strategy === 'default_role' && (
                        <p className="text-sm text-slate-500">
                          Rolle: {fallbackConfig.default_role_display} @ {fallbackConfig.default_tenant_name}
                        </p>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setShowFallbackDialog(true)}>
                      Bearbeiten
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Create Mapping Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Neues SSO-Mapping erstellen</DialogTitle>
            <DialogDescription>
              Verknüpfen Sie eine Entra ID Gruppe oder App Rolle mit einer Portal-Rolle
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Typ</Label>
              <Select
                value={newMapping.entra_type}
                onValueChange={(v) => setNewMapping({ ...newMapping, entra_type: v, portal_role_id: '', portal_tenant_id: '' })}
              >
                <SelectTrigger className="mt-1" style={{ backgroundColor: 'white' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="group">Gruppe (Mandantenspezifisch)</SelectItem>
                  <SelectItem value="app_role">App Rolle (Global)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Entra Name *</Label>
              <Input
                value={newMapping.entra_name}
                onChange={(e) => setNewMapping({ ...newMapping, entra_name: e.target.value })}
                placeholder={newMapping.entra_type === 'group' ? 'Portal_Mandant_Admin' : 'FinanceTeam'}
                className="mt-1"
                style={{ backgroundColor: 'white' }}
              />
              {newMapping.entra_type === 'group' && (
                <p className="text-xs text-slate-500 mt-1">
                  Empfohlen: Portal_[MandantSlug]_[Rolle]
                </p>
              )}
            </div>

            <div>
              <Label>Entra Object ID (optional)</Label>
              <Input
                value={newMapping.entra_id}
                onChange={(e) => setNewMapping({ ...newMapping, entra_id: e.target.value })}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="mt-1"
                style={{ backgroundColor: 'white' }}
              />
            </div>

            <div>
              <Label>Portal Rolle *</Label>
              <Select
                value={newMapping.portal_role_id}
                onValueChange={(v) => setNewMapping({ ...newMapping, portal_role_id: v })}
              >
                <SelectTrigger className="mt-1" style={{ backgroundColor: 'white' }}>
                  <SelectValue placeholder="Rolle auswählen" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {newMapping.entra_type === 'app_role' ? (
                    roles.global_roles?.map(r => (
                      <SelectItem key={r.role_id} value={r.role_id}>
                        {r.display_name}
                      </SelectItem>
                    ))
                  ) : (
                    roles.tenant_roles?.map(r => (
                      <SelectItem key={r.role_id} value={r.role_id}>
                        {r.display_name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {newMapping.entra_type === 'group' && (
              <div>
                <Label>Mandant *</Label>
                <Select
                  value={newMapping.portal_tenant_id}
                  onValueChange={(v) => setNewMapping({ ...newMapping, portal_tenant_id: v })}
                >
                  <SelectTrigger className="mt-1" style={{ backgroundColor: 'white' }}>
                    <SelectValue placeholder="Mandant auswählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {tenants.map(t => (
                      <SelectItem key={t.tenant_id} value={t.tenant_id}>
                        {t.name} ({t.slug})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label>Bei Login synchronisieren</Label>
              <Switch
                checked={newMapping.sync_on_login}
                onCheckedChange={(v) => setNewMapping({ ...newMapping, sync_on_login: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleCreateMapping}
              disabled={actionLoading}
              style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fallback Config Dialog */}
      <Dialog open={showFallbackDialog} onOpenChange={setShowFallbackDialog}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>Fallback-Konfiguration</DialogTitle>
            <DialogDescription>
              Definieren Sie das Verhalten für Benutzer ohne Rollen-Mapping
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Strategie</Label>
              <Select
                value={fallbackForm.strategy}
                onValueChange={(v) => setFallbackForm({ ...fallbackForm, strategy: v })}
              >
                <SelectTrigger className="mt-1" style={{ backgroundColor: 'white' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="default_role">Standard-Rolle zuweisen</SelectItem>
                  <SelectItem value="deny">Zugriff verweigern</SelectItem>
                  <SelectItem value="pending_approval">Admin-Genehmigung erforderlich</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {fallbackForm.strategy === 'default_role' && (
              <>
                <div>
                  <Label>Standard-Rolle</Label>
                  <Select
                    value={fallbackForm.default_role_id}
                    onValueChange={(v) => setFallbackForm({ ...fallbackForm, default_role_id: v })}
                  >
                    <SelectTrigger className="mt-1" style={{ backgroundColor: 'white' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {roles.tenant_roles?.map(r => (
                        <SelectItem key={r.role_id} value={r.role_id}>
                          {r.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Standard-Mandant</Label>
                  <Select
                    value={fallbackForm.default_tenant_id}
                    onValueChange={(v) => setFallbackForm({ ...fallbackForm, default_tenant_id: v })}
                  >
                    <SelectTrigger className="mt-1" style={{ backgroundColor: 'white' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {tenants.map(t => (
                        <SelectItem key={t.tenant_id} value={t.tenant_id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFallbackDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleSaveFallback}
              disabled={actionLoading}
              style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SSOMapppingPanel;
